import { useEffect, useState } from 'react';
import type * as React from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, getApiErrorMessage } from '../api';
import { Button, Card, Input, Loading, Logo } from '../ui';

type ResetState = 'checking' | 'ready' | 'invalid' | 'completed';

function errorMessage(error: unknown): string {
  const message = getApiErrorMessage(error, '密碼重設失敗，請稍後再試。');
  return error instanceof ApiError && error.requestId
    ? `${message}（錯誤編號：${error.requestId}）`
    : message;
}

export function InternalPasswordResetPage() {
  const [token] = useState(
    () => new URLSearchParams(window.location.search).get('token')?.trim() ?? '',
  );
  const [state, setState] = useState<ResetState>('checking');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
    if (!token) {
      setState('invalid');
      return;
    }
    api.auth
      .validatePasswordReset(token)
      .then((result) => setState(result.valid ? 'ready' : 'invalid'))
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    setSubmitting(true);
    try {
      await api.auth.completePasswordReset(token, password, confirmation);
      setPassword('');
      setConfirmation('');
      setState('completed');
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-sand px-4 py-10">
      <Card className="w-full max-w-lg p-7 md:p-9">
        <Logo />
        <h1 className="mt-8 text-2xl font-extrabold text-ink">重設內部帳號密碼</h1>

        {state === 'checking' && (
          <div className="mt-8 flex items-center gap-3 rounded-2xl bg-slate-50 p-5 text-sm text-slate-600">
            <Loading />
            <span>正在驗證一次性重設連結…</span>
          </div>
        )}

        {state === 'invalid' && (
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-700">
              此密碼重設連結無效、已使用或已過期，請重新取得一次性連結。
            </div>
            <Link className="inline-flex font-bold text-teal-700 hover:underline" to="/login">
              返回登入頁
            </Link>
          </div>
        )}

        {state === 'completed' && (
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-800">
              密碼已更新，所有舊的登入 Session 也已撤銷。請使用新密碼重新登入。
            </div>
            <Link
              className="inline-flex min-h-10 items-center justify-center rounded-xl bg-teal-600 px-4 py-2 text-sm font-bold text-white hover:bg-teal-700"
              to="/login"
            >
              前往登入
            </Link>
          </div>
        )}

        {state === 'ready' && (
          <form className="mt-6 space-y-5" onSubmit={submit}>
            <p className="text-sm leading-6 text-slate-600">
              請設定至少 12 個字元，並同時包含英文大寫、小寫、數字與符號；密碼不可包含登入帳號。
            </p>
            <Input
              label="新密碼"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              minLength={12}
              maxLength={200}
              required
            />
            <Input
              label="再次輸入新密碼"
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
              minLength={12}
              maxLength={200}
              required
            />
            {message && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                {message}
              </div>
            )}
            <Button className="w-full" loading={submitting}>
              儲存新密碼
            </Button>
          </form>
        )}
      </Card>
    </main>
  );
}
